// CLV Line Movement — "Beat the Line" board (CLV-2).
//
// Surfaces opening -> current Pinnacle 1X2 line movement from
// vw_clv_line_movement (clean single-source: fixture_market_odds_history).
// drift_pp > 0 = price firmed (money in). The board is intentionally thin
// today and deepens as the 6h odds sweep accrues snapshots — we never
// fabricate movement. No win-rate / methodology disclosure: outputs only.

import { useMemo } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { useClvLineMovement, type ClvMovementRow } from "../../exchange/useClvLineMovement.ts";

const GRID = "minmax(0,2.2fr) 70px 92px 92px 96px 64px";

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDrift(pp: number): string {
  const sign = pp > 0 ? "+" : "";
  return `${sign}${pp.toFixed(2)}pp`;
}

function driftCls(pp: number): "pos" | "neg" | "" {
  if (pp > 0.1) return "pos";
  if (pp < -0.1) return "neg";
  return "";
}

function selLabel(row: ClvMovementRow): string {
  const teams =
    row.home_team !== null && row.away_team !== null
      ? `${row.home_team} v ${row.away_team}`
      : row.fixture_id;
  return teams;
}

export function ClvLineMovementPage(): JSX.Element {
  const { rows, loading, error } = useClvLineMovement();

  // Sort by sharpest absolute movement first, then kickoff.
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const d = Math.abs(b.drift_pp) - Math.abs(a.drift_pp);
        if (d !== 0) return d;
        return a.kickoff_at.localeCompare(b.kickoff_at);
      }),
    [rows],
  );

  const stats = useMemo(() => {
    const fixtures = new Set(rows.map((r) => r.fixture_id)).size;
    const firmed = rows.filter((r) => r.drift_pp > 0.1).length;
    const sharpest = rows.reduce((m, r) => (Math.abs(r.drift_pp) > Math.abs(m) ? r.drift_pp : m), 0);
    return { tracked: rows.length, fixtures, firmed, sharpest };
  }, [rows]);

  return (
    <div className="page" data-screen-label="CLV Line Movement">
      <div className="ex-head">
        <div>
          <h1>Line Movement · beat the line</h1>
          <div className="sub">
            How the Pinnacle line has moved since it opened. A firming price (probability shortening)
            is where the sharp money is going — capturing it early is closing-line value.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag phantom">
            <span className="dot" />
            Pinnacle 1X2
          </span>
          <span className="tag">Open → Now</span>
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          Movement is tracked from a single clean odds feed and deepens as the intraday sweep runs.
          Fixtures appear here once they have at least two genuine snapshots — we never invent a move.
        </span>
      </div>

      <div className="ex-stats">
        <div className="ex-stat">
          <div className="k">Selections tracked</div>
          <div className="v">{stats.tracked}</div>
          <div className="delta">{stats.fixtures} fixtures with movement</div>
        </div>
        <div className="ex-stat">
          <div className="k">Firming lines</div>
          <div className="v">{stats.firmed}</div>
          <div className="delta">price shortening (money in)</div>
        </div>
        <div className="ex-stat">
          <div className="k">Sharpest move</div>
          <div className={`v`}>
            {stats.sharpest !== 0 ? fmtDrift(stats.sharpest) : "—"}
          </div>
          <div className="delta">since open</div>
        </div>
      </div>

      <div className="ex-table">
        <div className="ex-thead" style={{ gridTemplateColumns: GRID }}>
          <span>Fixture</span>
          <span>Sel.</span>
          <span>Open</span>
          <span>Now</span>
          <span>Drift ↓</span>
          <span>Snaps</span>
        </div>

        {loading && rows.length === 0 ? (
          <div className="ex-row" style={{ gridTemplateColumns: "1fr" }}>
            <div style={{ padding: "16px 12px", color: "var(--text-secondary)" }}>
              Loading line movement…
            </div>
          </div>
        ) : error !== null ? (
          <div className="ex-row" style={{ gridTemplateColumns: "1fr" }}>
            <div style={{ padding: "16px 12px", color: "var(--text-secondary)" }}>
              Line movement unavailable: {error}
            </div>
          </div>
        ) : sorted.length === 0 ? (
          <div className="ex-row" style={{ gridTemplateColumns: "1fr" }}>
            <div style={{ padding: "16px 12px", color: "var(--text-secondary)" }}>
              No tracked movement yet. As the intraday odds sweep accrues a second snapshot per
              fixture, opening → current moves will appear here.
            </div>
          </div>
        ) : (
          sorted.map((r) => (
            <div
              className="ex-row"
              key={`${r.fixture_id}:${r.selection}`}
              style={{ gridTemplateColumns: GRID }}
            >
              <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                <div style={{ fontWeight: 600 }}>{selLabel(r)}</div>
                <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                  {fmtClock(r.kickoff_at)}
                </div>
              </div>
              <div style={{ textTransform: "capitalize" }}>{r.selection}</div>
              <div>{r.open_odds.toFixed(2)}</div>
              <div>{r.cur_odds.toFixed(2)}</div>
              <div className={`delta ${driftCls(r.drift_pp)}`}>{fmtDrift(r.drift_pp)}</div>
              <div>{r.n_snaps}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
